/**
 * 顧客管理画面用サービス（/:raceAlias/admin/）
 * 大会側の管理者（admin ロール）が使用
 */

import { collection, doc, getDocs, orderBy, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';

import type { AdminClaim, AdminRunner, ClaimStatus, MintingStats, MintTask, Runner } from '../types';
import { db } from './firebase';

/** claims 一覧取得（管理画面用） */
export async function getClaims(raceAlias: string): Promise<AdminClaim[]> {
  const claimsRef = collection(db, 'claims');
  const q = query(
    claimsRef,
    where('raceAlias', '==', raceAlias),
    where('deletedAt', '==', null),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (docData) =>
      ({
        id: docData.id,
        ...docData.data(),
      }) as AdminClaim
  );
}

/** ミント状況の集計 */
export function calculateMintingStats(claims: AdminClaim[]): MintingStats {
  const stats: MintingStats = {
    total: 0,
    // ステータス別
    ready: 0,
    'video-queued': 0,
    'video-processing': 0,
    'video-done': 0,
    'video-failed': 0,
    'mint-queued': 0,
    'mint-processing': 0,
    'mint-pending': 0,
    'mint-failed': 0,
    completed: 0,
    'permanently-failed': 0,
    // コンテキスト別
    online: 0,
    on_site: 0,
    patterns: {},
  };

  for (const claim of claims) {
    stats.total++;
    stats[claim.mint_context]++;

    // ステータス別カウント
    const statusKey = claim.status as keyof MintingStats;
    if (statusKey in stats && typeof stats[statusKey] === 'number') {
      (stats[statusKey] as number)++;
    }

    if (claim.status === 'completed' && claim.sourceFileName) {
      const pattern = claim.sourceFileName.split('.')[0];
      stats.patterns[pattern] = (stats.patterns[pattern] || 0) + 1;
    }
  }

  return stats;
}

/** claim のステータス更新 */
export async function updateClaimStatus(claimId: string, status: ClaimStatus): Promise<void> {
  const claimRef = doc(db, 'claims', claimId);
  await updateDoc(claimRef, {
    status,
    updatedAt: serverTimestamp(),
  });
}

/** 動画生成のリトライ（video-failed → video-queued） */
export async function retryVideoGeneration(claimId: string): Promise<void> {
  const claimRef = doc(db, 'claims', claimId);
  await updateDoc(claimRef, {
    status: 'video-queued' as ClaimStatus,
    videoRetryCount: 0, // リセット
    lastError: null,
    updatedAt: serverTimestamp(),
  });
}

/** ミントのリトライ（mint-failed/permanently-failed → mint-queued） */
export async function retryMint(claimId: string): Promise<void> {
  const claimRef = doc(db, 'claims', claimId);
  await updateDoc(claimRef, {
    status: 'mint-queued' as ClaimStatus,
    mintRetryCount: 0, // リセット
    lastError: null,
    updatedAt: serverTimestamp(),
  });
}

/** ミントワーカーのステータスリセット（Revoke） */
export async function revokeMintWorker(workerIndex: string): Promise<void> {
  const workerRef = doc(db, 'mintWorkers', workerIndex);

  await updateDoc(workerRef, {
    status: 'pending',
    updatedAt: serverTimestamp(),
  });

  await updateDoc(workerRef, {
    status: 'waiting',
    updatedAt: serverTimestamp(),
  });
}

/** runners 一覧取得（計測データ管理画面用） */
export async function getRunners(raceAlias: string): Promise<AdminRunner[]> {
  const runnersRef = collection(db, 'runners');
  const q = query(runnersRef, where('raceAlias', '==', raceAlias), orderBy('updatedAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (docData) =>
      ({
        id: docData.id,
        ...docData.data(),
      }) as AdminRunner
  );
}

/** locationIdが飛んでいないかチェック（1,3があるけど2がない等）
 * ※ locationId 6は任意の中間地点なので、6だけスキップしている場合は欠損扱いにしない
 */
export function hasTimingGaps(timing: { locationId: string }[] | null): boolean {
  if (!timing || timing.length <= 1) return false;

  const ids = timing
    .map((t) => Number.parseInt(t.locationId, 10))
    .filter((id) => !Number.isNaN(id))
    .sort((a, b) => a - b);

  if (ids.length <= 1) return false;

  // 連続しているかチェック（locationId 6は任意なのでスキップOK）
  for (let i = 1; i < ids.length; i++) {
    const prev = ids[i - 1];
    const curr = ids[i];

    if (curr !== prev + 1) {
      // ギャップがある場合、6だけがスキップされているかチェック
      for (let missing = prev + 1; missing < curr; missing++) {
        if (missing !== 6) {
          // 6以外が欠損している場合は真の欠損
          return true;
        }
      }
    }
  }
  return false;
}

/** データ欠損のある runners を取得（locationIdが飛んでいる） */
export async function getRunnersWithMissingTiming(raceAlias: string): Promise<AdminRunner[]> {
  const runners = await getRunners(raceAlias);
  return runners.filter((runner) => hasTimingGaps(runner.timing));
}

/** runners 一覧取得（raceId指定） */
export async function getRaceRunners(raceId: string): Promise<Runner[]> {
  const runnersRef = collection(db, 'runners');
  const q = query(runnersRef, where('raceId', '==', raceId));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (docData) =>
      ({
        id: docData.id,
        ...docData.data(),
      }) as Runner
  );
}

/** 未登録ランナーを取得（timing はあるが登録情報がない） */
export async function getUnregisteredRunners(raceAlias: string): Promise<AdminRunner[]> {
  const runners = await getRunners(raceAlias);
  // timing はあるが fullName がない = 登録情報なし（招待選手は fullName があるので除外される）
  return runners.filter((runner) => runner.timing && runner.timing.length > 0 && !runner.fullName);
}

/** ehimeMintTasks 一覧取得（手数料管理用） */
export async function getMintTasks(): Promise<MintTask[]> {
  const tasksRef = collection(db, 'ehimeMintTasks');
  const q = query(tasksRef, orderBy('createdAt'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (docData) =>
      ({
        id: docData.id,
        ...docData.data(),
      }) as MintTask
  );
}

/**
 * 現在の環境がテストネットかどうかを判定
 * dev-nft, demo-nft → testnet / nft → mainnet
 */
function isTestnet(): boolean {
  const hostname = window.location.hostname;
  return hostname.startsWith('dev-') || hostname.startsWith('demo-');
}

/** ブロックチェーンエクスプローラーURL取得 */
export function getExplorerUrl(_chain: string, txHash: string): string {
  // _chain は後方互換性のために残しているが、HeraldingEraではSoneium固定
  const testnet = isTestnet();
  const baseUrl = testnet ? 'https://soneium-minato.blockscout.com' : 'https://soneium.blockscout.com';
  return `${baseUrl}/tx/${txHash}`;
}
